<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { isExperimentAsync } from '@libs/feature-toggle';

import {
  ItinerarySegmentType,
  isItineraryLocationLoaderSegment,
  isItineraryDateLocationLoaderSegment,
  isItinerarySimpleTransportLoaderSegment,
  isItineraryBookableProductLoaderSegment,
} from '../../../models';
import { useTripOverviewPageStore } from '../../../stores';
import AddDestinationPlaceholder from '../AddDestinationPlaceholder.vue';
import ItinerarySimpleTransportSegment from '../ItinerarySimpleTransportSegment.vue';
import ItineraryNoTransportSegment from '../ItineraryNoTransportSegment.vue';
import ItineraryLocationSegment from '../location/ItineraryLocationSegment.vue';
import ItineraryDateLocationSegment from '../location/ItineraryDateLocationSegment.vue';
import ItineraryOwnStaySegment from '../ownStay/ItineraryOwnStaySegment.vue';
import ItineraryUpsellNoTransportSegment from '../ItineraryUpsellNoTransportSegment.vue';
import { GlobalSuggestionTypeSwitch } from '../../globalSuggestionType';
import AccommodationUpsellSegment from '../upsell/accommodation/AccommodationUpsellSegment.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';

import ItineraryProductLoaderSegment from './ItineraryProductLoaderSegment.vue';
import ItineraryUpsellingLoader from './ItineraryUpsellingLoader.vue';

const tripOverviewPageStore = useTripOverviewPageStore();
const overviewPlaceholderNoTransportExperiment = computedAsync<boolean>(
  () => isExperimentAsync('overview_placeholder_no_transport_experiment'),
  false
);

const isTopAddDestinationOverviewButtonExperiment = computedAsync<boolean>(
  () => isExperimentAsync('top_add_destination_overview_experiment'),
  false
);

const browserStore = useBrowserStore();
</script>

<template>
  <div class="trip-itinerary-loader">
    <div class="global-suggestion">
      <GlobalSuggestionTypeSwitch v-if="browserStore.isSmallerThan1200px" />
    </div>
    <div
      v-for="(segment, index) in tripOverviewPageStore.tripItineraryLoader"
      :key="index"
      :class="{
        'trip-itinerary-first-segment': index === 0,
      }"
    >
      <ItineraryTimeline
        v-if="isTopAddDestinationOverviewButtonExperiment && index === 0"
        :segment-type="ItinerarySegmentType.StartLocation"
      >
        <template #banner>
          <AddDestinationPlaceholder origin="topTest" class="test" />
        </template>
      </ItineraryTimeline>
      <ItineraryDateLocationSegment
        v-if="isItineraryDateLocationLoaderSegment(segment)"
        :segment="segment"
        :print-mode="false"
        is-loading
      />
      <ItineraryLocationSegment
        v-if="isItineraryLocationLoaderSegment(segment)"
        :segment="segment"
        :print-mode="false"
      />

      <ItineraryNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.NoTransport &&
          (!overviewPlaceholderNoTransportExperiment ||
            (overviewPlaceholderNoTransportExperiment &&
              tripOverviewPageStore.hasUserSelectedTransport))
        "
        :segment="segment"
        :print-mode="false"
        is-loading
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />

      <ItineraryUpsellingLoader
        v-if="segment.segmentType === ItinerarySegmentType.Upsell"
        :print-mode="false"
        :segment="segment"
      />

      <ItineraryUpsellNoTransportSegment
        v-if="segment.segmentType === ItinerarySegmentType.UpsellNoTransport"
        :print-mode="false"
        :segment="segment"
        is-loading
      />

      <ItinerarySimpleTransportSegment
        v-if="isItinerarySimpleTransportLoaderSegment(segment)"
        :segment="segment"
        is-loading-transport
        :print-mode="false"
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />

      <ItineraryOwnStaySegment
        v-if="segment.segmentType === ItinerarySegmentType.OwnStay"
        :segment="segment"
        :print-mode="false"
        is-loading
      />

      <ItineraryProductLoaderSegment
        v-if="isItineraryBookableProductLoaderSegment(segment)"
        :segment="segment"
      />

      <AccommodationUpsellSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.AccommodationUpsellLoader
        "
        :segment="segment"
        :print-mode="false"
        is-loading
      />
    </div>
    <AddDestinationPlaceholder origin="bottom" />
  </div>
</template>

<style lang="scss" scoped>
.trip-itinerary-loader {
  .global-suggestion {
    box-shadow: var(--shadow-elevation-200);
    position: relative;
    @include for-more-or-equal-than-1200-px {
      position: sticky;
      top: 0;
      z-index: var(--modal-overlay-z-index);
    }
  }
  .trip-itinerary-first-segment {
    @include for-less-or-equal-than-1199-px {
      margin-top: var(--spacing-24);
    }
  }
  .test {
    margin: 0;
    @include for-less-or-equal-than-1200-px {
      margin-left: var(--spacing-8);
    }
    @include for-less-or-equal-than-600-px {
      margin-left: var(--spacing-0);
    }
  }
}
</style>
